import { domToCanvas, domToPng } from "modern-screenshot";

/** A capture, with the size it had on screen in CSS pixels (the image itself is `scale` times that). */
export interface Shot {
  src: string;
  width: number;
  height: number;
  /** The colour behind it on the page, to pad it with. */
  background: string;
}

const STUCK = "data-capture-stuck";
const PINNED = "data-capture-pinned";

/** The first solid background at or behind an element, so a transparent card doesn't come out see-through. */
function backgroundBehind(el: Element): string {
  const win = el.ownerDocument.defaultView!;
  for (let n: Element | null = el; n; n = n.parentElement) {
    const bg = win.getComputedStyle(n).backgroundColor;
    if (bg && bg !== "transparent" && !/rgba\(.*,\s*0\)$/.test(bg)) return bg;
  }
  return win.getComputedStyle(el.ownerDocument.documentElement).backgroundColor || "#fff";
}

/**
 * What a frame's window shows right now, scrolled position included. The page is drawn from the
 * top down to the bottom of the view and cropped, so whatever sits still on screen while the page
 * scrolls is moved down by the scroll to sit where it appears: sticky elements stuck to the top
 * (the sidebar; they all start at the top of the page in the dashboard), and fixed ones (an open
 * dialog and its backdrop), which would otherwise be drawn at the top of the page.
 */
export async function shootScreen(win: Window, scale = 2): Promise<Shot> {
  await settled(win);
  const doc = win.document;
  const width = doc.documentElement.clientWidth;
  const height = win.innerHeight;
  const y = Math.round(win.scrollY);
  const all = y > 0 ? [...doc.body.querySelectorAll<HTMLElement>("*")] : [];
  const stuck = all.filter((el) => {
    const cs = win.getComputedStyle(el);
    return cs.position === "sticky" && Math.abs(el.getBoundingClientRect().top - (parseFloat(cs.top) || 0)) < 1;
  });
  const fixed = all.filter((el) => win.getComputedStyle(el).position === "fixed");
  // Only the outermost: one inside another moves with it.
  const pinned = fixed.filter((el) => !fixed.some((outer) => outer !== el && outer.contains(el)));
  stuck.forEach((el) => el.setAttribute(STUCK, ""));
  pinned.forEach((el) => el.setAttribute(PINNED, ""));
  try {
    const full = await domToCanvas(doc.body, {
      scale,
      width,
      height: y + height,
      backgroundColor: backgroundBehind(doc.body),
      onCloneEachNode: (node) => {
        if (!(node instanceof (win as unknown as typeof globalThis).HTMLElement)) return;
        if (node.hasAttribute(STUCK)) {
          node.style.position = "relative";
          node.style.top = `${y}px`;
        } else if (node.hasAttribute(PINNED)) {
          // `translate` adds to any transform the element already has instead of replacing it.
          node.style.translate = `0 ${y}px`;
        }
      },
    });
    const out = doc.createElement("canvas");
    out.width = Math.round(width * scale);
    out.height = Math.round(height * scale);
    out.getContext("2d")!.drawImage(full, 0, y * scale, out.width, out.height, 0, 0, out.width, out.height);
    return { src: out.toDataURL("image/png"), width, height, background: backgroundBehind(doc.body) };
  } finally {
    stuck.forEach((el) => el.removeAttribute(STUCK));
    pinned.forEach((el) => el.removeAttribute(PINNED));
  }
}

/**
 * Waits (up to `timeout`) until the framed dashboard has nothing loading: no requests in flight and
 * no skeletons. Then a moment more for charts to finish drawing.
 */
export async function settled(win: Window, timeout = 20_000) {
  const busy = (win as Window & { metrickleBusy?: () => number }).metrickleBusy;
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const loading = (busy?.() ?? 0) > 0 || win.document.querySelector(".animate-pulse, [aria-busy='true']");
    if (!loading) break;
    await new Promise((r) => setTimeout(r, 150));
  }
  await new Promise((r) => setTimeout(r, 400));
}

/** One element on its own, sharper than the screen shot because the card shows it larger. */
export async function shootElement(el: HTMLElement, scale = 3): Promise<Shot> {
  await settled(el.ownerDocument.defaultView!);
  const r = el.getBoundingClientRect();
  const background = backgroundBehind(el);
  const src = await domToPng(el, { scale, backgroundColor: background });
  return { src, width: r.width, height: r.height, background };
}

/**
 * The finished share card, at `scale` times its layout size. The size is passed explicitly: the
 * preview shows the card shrunk with a transform, and measuring it on screen would export it shrunk.
 */
export async function renderCard(card: HTMLElement, scale = 2): Promise<Blob> {
  const url = await domToPng(card, { scale, width: card.offsetWidth, height: card.offsetHeight });
  return (await fetch(url)).blob();
}

/** Clicks can land on a tiny span; a close-up wants the block it belongs to. */
export function meaningfulBlock(el: Element | null, minW = 120, minH = 48): HTMLElement | null {
  let n = el as HTMLElement | null;
  while (n && n !== n.ownerDocument.body) {
    const r = n.getBoundingClientRect();
    if (r.width >= minW && r.height >= minH) return n;
    n = n.parentElement;
  }
  return null;
}

/** "Card “Release v4.12 · Checkout”", to say what's picked without showing it. */
export function describeElement(el: HTMLElement): string {
  const label = el.getAttribute("aria-label") ?? el.querySelector("h1,h2,h3,h4,caption,legend")?.textContent ?? el.textContent ?? "";
  const text = label.replace(/\s+/g, " ").trim();
  const r = el.getBoundingClientRect();
  return `${text ? `“${text.length > 48 ? `${text.slice(0, 47)}…` : text}”` : `<${el.tagName.toLowerCase()}>`}, ${Math.round(r.width)}×${Math.round(r.height)}`;
}
